import { Widget } from './form-widget.model';
import { parseLocalDateString } from '../helpers/date-utils';
import moment from 'moment';

export interface VitalsMetadata {
  id: string;
  treatment_id: string;
  height_ft: number;
  height_in: number;
  weight: number;
  goal_weight: number;
  body_fat_percentage: number;
  prev_weight: number;
  blood_pressure: string;
  pulse: number;
  bmr: number;
  respiration: number;
  tdee: number;
  fitzpatrick_scale: string;
  waist: number;
  hip: number;
  chest_bust: number;
  left_arm: number;
  right_arm: number;
  left_thigh: number;
  right_thigh: number;
  neck: number;
  vtc: number;
  start_weight: number;
  last_menstrual?: Date | string;
  vital_notes?: string;
}

type VitalsFieldSpec = {
  transform: (value: unknown) => string | number | null;
};

export class FbEmrVitalsWidget extends Widget {
  private readonly VITALS_SCHEMA: { [K in keyof VitalsMetadata]: VitalsFieldSpec } = {
    id: { transform: (value) => value as string },
    treatment_id: { transform: (value) => value as string },
    blood_pressure: { transform: (value) => this.asTrimmedString(value) },
    bmr: { transform: (value) => this.asNumber(value) },
    body_fat_percentage: { transform: (value) => this.asNumber(value) },
    chest_bust: { transform: (value) => this.asNumber(value) },
    fitzpatrick_scale: { transform: (value) => value as string },
    goal_weight: { transform: (value) => this.asNumber(value) },
    height_ft: { transform: (value) => this.asNumber(value) },
    height_in: { transform: (value) => this.asNumber(value) },
    hip: { transform: (value) => this.asNumber(value) },
    left_arm: { transform: (value) => this.asNumber(value) },
    left_thigh: { transform: (value) => this.asNumber(value) },
    neck: { transform: (value) => this.asNumber(value) },
    prev_weight: { transform: (value) => this.asNumber(value) },
    pulse: { transform: (value) => this.asNumber(value) },
    respiration: { transform: (value) => this.asNumber(value) },
    right_arm: { transform: (value) => this.asNumber(value) },
    right_thigh: { transform: (value) => this.asNumber(value) },
    start_weight: { transform: (value) => this.asNumber(value) },
    tdee: { transform: (value) => this.asNumber(value) },
    vtc: { transform: (value) => this.asNumber(value) },
    waist: { transform: (value) => this.asNumber(value) },
    weight: { transform: (value) => this.asNumber(value) },
    last_menstrual: { transform: (value) => this.parseDateOrEmpty(value) },
    vital_notes: { transform: (value) => this.asTrimmedString(value) },
  };

  constructor(data?: unknown) {
    super(data);

    this.name = 'fb-emr-vitals';
    this.title = 'Vitals';

    this.properties.text ??= { title: 'Header', value: 'Vitals' };
  }

  public getMetadata(): Record<string, unknown> {
    const value = this.properties?.value ?? this.properties?.items?.value;

    if (!value) {
      return { vitals: {} };
    }

    const vitals = this.buildVitals(value as Partial<VitalsMetadata>);
    const isValid = vitals && Object.keys(vitals).length > 0;

    return isValid ? { vitals } : { vitals: {} };
  }

  public isValid(): boolean {
    const invalid = this.properties?.formInvalid ?? this.properties?.items?.formInvalid;
    return !invalid;
  }

  private buildVitals(data: Partial<VitalsMetadata>): Partial<VitalsMetadata> {
    return Object.keys(this.VITALS_SCHEMA).reduce((acc, field) => {
      const schema = this.VITALS_SCHEMA[field];
      const raw = data[field];
      const value = schema.transform(raw);

      if (this.hasValue(value)) {
        acc[field] = value;
      }

      return acc;
    }, {} as Partial<VitalsMetadata>);
  }

  private hasValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return true;
  }

  private asTrimmedString(v: unknown): string | null {
    if (v === null || v === undefined) {
      return null;
    }

    return String(v).trim();
  }

  private asNumber(v: unknown): number {
    if (v === null) {
      return 0;
    }

    const n = typeof v === 'number' ? v : Number(String(v).trim());

    return Number.isFinite(n) ? n : null;
  }

  private parseDateOrEmpty(dateValue: unknown): string | null {
    return dateValue instanceof Date || typeof dateValue === 'string' || moment.isMoment(dateValue)
      ? parseLocalDateString(dateValue)
      : null;
  }
}
